import { useState } from 'react'
import { Deferred, Head, router } from '@inertiajs/react'
import { Search } from 'lucide-react'
import { AuditLogActionFilter } from '@/frontend/components/AuditLogActionFilter'
import { buildExcludedParam } from '@/frontend/lib/auditLogs'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import WorkerPortalShell from '@/frontend/layouts/worker-first/WorkerPortalShell'
import AuditLogList from '@/frontend/pages/Dashboard/AuditLogs/AuditLogList'
import AuditOutcomes, { type AuditOutcomesData } from '@/frontend/components/DigitalWorkers/AuditOutcomes'
import type { ScopeWorker } from '@/frontend/types/inertia'
import type { DigitalWorkerDetail } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  page_title: string
  audit_logs: Array<{
    id: string
    audit_action: string
    description: string
    details: Record<string, unknown>
    created_at: string
    actor_name: string | null
    worker_name: string | null
    worker_avatar_url: string | null
  }>
  pagination: { current_page: number; total_pages: number; total_count: number; per_page: number }
  filters: {
    workers: Array<{ id: string; name: string }>
  }
  current_filters: { excluded_actions: string[]; digital_worker_ids: string[]; search: string }
  scope_worker?: ScopeWorker
  // Ficha completa do agente em agent-scope: alimenta o FichaHeader dentro do
  // WorkerPortalShell (a moldura do portal). Ausente no modo org-level.
  scope_worker_header?: DigitalWorkerDetail
  // Desfechos das conversas (SZ-1600, C.5): prop deferida, presente só em
  // agent-scope (rota canônica /digital_workers/:id/auditoria — C.2).
  audit_outcomes?: AuditOutcomesData | null
}

function AuditLogsIndex({ page_title, audit_logs, pagination, filters, current_filters, scope_worker, scope_worker_header, audit_outcomes = null }: Props) {
  const [query, setQuery] = useState(current_filters.search)

  const navigate = (params: Record<string, string | string[] | number>) => {
    // Explicitly changing the worker filter dropdown leaves agent-scope mode
    // (back to the org URL); every other navigation (search/pagination) stays
    // on the canonical agent URL (C.2, SZ-1597).
    const keepScope = scope_worker && !('digital_worker_ids' in params)
    const basePath = keepScope
      ? `/dashboard/digital_workers/${scope_worker.id}/auditoria`
      : '/dashboard/audit_logs'
    router.get(basePath, {
      excluded_actions: buildExcludedParam(current_filters.excluded_actions),
      // The canonical URL already names the agent; echoing the mirrored
      // plural filter back would just pollute it.
      digital_worker_ids: keepScope ? undefined : current_filters.digital_worker_ids,
      search: current_filters.search,
      page: 1,
      ...params,
    }, { preserveState: true })
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    navigate({ search: query })
  }

  const body = (
    <>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          {/* Título de página do Painel: 19/650 (escala do DS). */}
          <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-foreground">{page_title}</h1>
          <p className="text-[13px] text-muted-foreground mt-1">
            {scope_worker
              ? `Histórico de ações operacionais de ${scope_worker.name}.`
              : 'Histórico de ações operacionais da organização.'}
          </p>
        </div>

        <AuditLogActionFilter
          excludedActions={current_filters.excluded_actions}
          onExcludedActionsChange={(excluded) => navigate({ excluded_actions: buildExcludedParam(excluded) })}
          workers={filters.workers}
          digitalWorkerIds={current_filters.digital_worker_ids}
          onDigitalWorkerIdsChange={(ids) => navigate({ digital_worker_ids: ids })}
        />
      </div>

      {/* Painel por desfecho (SZ-1600, C.5): só em agent-scope, acima do log
          de eventos — remontado aqui depois que a seção auditoria do Show
          deixou de existir (C.2, SZ-1597). */}
      {scope_worker && (
        <Deferred
          data="audit_outcomes"
          fallback={
            <p className="py-4 text-[13px] text-muted-foreground">Carregando desfechos das conversas…</p>
          }
        >
          <AuditOutcomes data={audit_outcomes ?? null} />
        </Deferred>
      )}

      <form onSubmit={handleSearch} className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            aria-label="Buscar no conteúdo dos eventos"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por comando, ferramenta, credencial..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background"
          />
        </div>
        <button
          type="submit"
          className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-accent"
        >
          Buscar
        </button>
      </form>

      <AuditLogList
        audit_logs={audit_logs}
        pagination={pagination}
        onPageChange={(page) => navigate({ page })}
        emptyMessage={scope_worker ? 'Nenhum evento com este agente.' : undefined}
      />
    </>
  )

  return (
    <>
      <Head title={page_title} />
      {/* Em agent-scope a página vive dentro da moldura do portal. Auditoria é
          grupo de página única, então WorkerPortalNav não renderiza subnav; o
          grupo ativo aparece no disclosure da sidebar. No modo org-level a
          página segue seca num container simples — comportamento intocado. */}
      {scope_worker_header ? (
        <WorkerPortalShell worker={scope_worker_header} section="auditoria">{body}</WorkerPortalShell>
      ) : (
        <div className="space-y-6">{body}</div>
      )}
    </>
  )
}

AuditLogsIndex.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>

export default AuditLogsIndex
